import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Autocomplete,
  Button,
  Divider,
} from '@mui/material';
import { LocalHospital, ArrowBack, ExitToApp, Print } from '@mui/icons-material';
import { patientService, userService, printSettingsService, authService, issuedCertificateService } from '../../services/api';
import { generateCertNo } from '../../utils/certNumber';

const calculateAge = (dob) => {
  if (!dob) return '';
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (
    today.getMonth() - birth.getMonth() < 0 ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  ) age--;
  return age;
};

const fmt = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const todayStr = () => new Date().toISOString().split('T')[0];

const FitnessCertificateGeneral = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = useState(null);

  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [printSettings, setPrintSettings] = useState(null);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  const [form, setForm] = useState(() => ({
    certNo: generateCertNo('FC'),
    date: todayStr(),
    examDate: todayStr(),
    purpose: '',
    remarks: '',
  }));

  const printRef = useRef();

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, dRes, sRes] = await Promise.all([
          patientService.getAllPatients(),
          userService.getDoctors(),
          printSettingsService.getSettings(),
        ]);
        setPatients(pRes.data);
        setDoctors(dRes.data);
        setPrintSettings(sRes.data);
        if (user?.role === 'Doctor') {
          const match = dRes.data.find(d => d.id === user.id);
          if (match) setSelectedDoctor(match);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      }
    };
    load();
  }, []);

  const handleField = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const isPrePrinted = (printSettings?.letterheadType || printSettings?.LetterheadType || 'Pre-Printed Letterhead') === 'Pre-Printed Letterhead';
  const mt = printSettings?.marginTop    ?? printSettings?.MarginTop    ?? 10;
  const mb = printSettings?.marginBottom ?? printSettings?.MarginBottom ?? 10;
  const ml = printSettings?.marginLeft   ?? printSettings?.MarginLeft   ?? 10;
  const mr = printSettings?.marginRight  ?? printSettings?.MarginRight  ?? 10;

  const handlePrint = async () => {
    try {
      await issuedCertificateService.save({
        certificateNo:       form.certNo,
        certificateType:     'FC',
        certificateTypeName: 'Fitness Certificate (General)',
        patientId:           selectedPatient?.id || null,
        patientName:         selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : '',
        doctorId:            selectedDoctor?.id || null,
        doctorName:          selectedDoctor ? `Dr. ${selectedDoctor.firstName} ${selectedDoctor.lastName}` : '',
        certDate:            form.date || new Date().toISOString().split('T')[0],
        details:             JSON.stringify({ examDate: form.examDate, purpose: form.purpose, remarks: form.remarks }),
      });
    } catch (err) { console.error('Failed to save certificate record:', err); }

    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>Fitness Certificate (General)</title>
          <style>
            body { font-family: Arial, sans-serif; margin: ${mt}mm ${mr}mm ${mb}mm ${ml}mm; font-size: 13px; color: #000; }
            * { text-transform: uppercase; }
            .fcg-title { text-align: center; font-weight: bold; letter-spacing: 0.5px; }
            .fcg-right { text-align: right; margin-top: 16px; }
          </style>
        </head>
        <body>${printRef.current.innerHTML}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const patientName = selectedPatient
    ? `${selectedPatient.firstName} ${selectedPatient.lastName}`
    : '______________________';
  const patientAge = selectedPatient ? calculateAge(selectedPatient.dateOfBirth) : '___';
  const patientGender = selectedPatient?.gender || '___';
  const doctorName = selectedDoctor
    ? `Dr. ${selectedDoctor.firstName} ${selectedDoctor.lastName}`
    : '______________________';
  const qualification = selectedDoctor?.education || selectedDoctor?.specialization || '______________________';
  const regNo = selectedDoctor?.medicalRegistrationNumber || '______________________';
  const hospital = printSettings?.hospitalName || 'Vedanta Hospital';
  const hospitalAddress = printSettings?.address || '';
  const hospitalPhone = printSettings?.phone || '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/reports')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Fitness Certificate (General)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>
            <IconButton size="large" onClick={(e) => setAnchorEl(e.currentTarget)} color="inherit">
              <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { authService.logout(); navigate('/'); }}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', gap: 2, p: 3, alignItems: 'flex-start' }}>

        {/* Left Panel — 30% */}
        <Paper elevation={3} sx={{ width: '30%', flexShrink: 0, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            Certificate Details
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Autocomplete
              options={patients}
              getOptionLabel={(p) => `${p.firstName} ${p.lastName}`}
              value={selectedPatient}
              onChange={(_, val) => setSelectedPatient(val)}
              renderInput={(params) => (
                <TextField {...params} label="Select Patient" size="small" required />
              )}
            />

            <Autocomplete
              options={doctors}
              getOptionLabel={(d) => `Dr. ${d.firstName} ${d.lastName}`}
              value={selectedDoctor}
              onChange={(_, val) => setSelectedDoctor(val)}
              renderInput={(params) => (
                <TextField {...params} label="Doctor" size="small" required />
              )}
            />

            <TextField
              label="Certificate No."
              size="small"
              value={form.certNo}
              onChange={handleField('certNo')}
            />

            <TextField
              label="Date"
              type="date"
              size="small"
              value={form.date}
              onChange={handleField('date')}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Examination Date"
              type="date"
              size="small"
              value={form.examDate}
              onChange={handleField('examDate')}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Purpose of Certificate"
              size="small"
              placeholder="e.g. Sports, School, Travel"
              value={form.purpose}
              onChange={handleField('purpose')}
            />

            <TextField
              label="Remarks (if any)"
              size="small"
              multiline
              rows={2}
              value={form.remarks}
              onChange={handleField('remarks')}
            />

            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{ mt: 1, background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)' }}
            >
              Print Certificate
            </Button>
          </Box>
        </Paper>

        {/* Right Panel — 70% preview */}
        <Paper elevation={3} sx={{ flex: 1, p: 4, borderRadius: 2, minHeight: 600 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Preview
          </Typography>

          <Box ref={printRef} sx={{ fontFamily: 'Arial, sans-serif', fontSize: '0.92rem', color: '#000', lineHeight: 1.9 }}>

            {/* Hospital header — hidden for Pre-Printed Letterhead */}
            {!isPrePrinted && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                  [{hospital}{hospitalAddress ? `, ${hospitalAddress}` : ''}{hospitalPhone ? `, ${hospitalPhone}` : ''}]
                </Typography>
              </Box>
            )}

            <Typography className="fcg-title" sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 2, textAlign: 'center', letterSpacing: 0.5 }}>
              FITNESS CERTIFICATE (GENERAL)
            </Typography>

            <Divider sx={{ mb: 2, borderColor: '#000' }} />

            <Typography sx={{ mb: 2 }}>
              This is to certify that Mr./Ms.{' '}
              <strong>{patientName}</strong>{' '}
              (Age: <strong>{patientAge}</strong>, Gender: <strong>{patientGender}</strong>) was examined on{' '}
              <strong>{form.examDate ? fmt(form.examDate) : '______'}</strong>.
            </Typography>

            <Typography sx={{ mb: 2 }}>
              On examination, he/she is found to be in good health and is{' '}
              <strong>physically and medically fit</strong>
              {form.purpose ? (
                <> for <strong>{form.purpose}</strong></>
              ) : (
                ' for the stated purpose'
              )}.
            </Typography>

            {form.remarks && (
              <Typography sx={{ mb: 2 }}>
                Remarks: <strong>{form.remarks}</strong>
              </Typography>
            )}

            <Typography>Date: {form.date ? fmt(form.date) : '______'}</Typography>
            <Typography sx={{ mb: 3 }}>Certificate No: {form.certNo || '______'}</Typography>

            <Divider sx={{ mb: 2, borderColor: '#ccc' }} />

            <div className="fcg-right" style={{ textAlign: 'right', marginTop: 16 }}>
              <Typography className="fcg-right" sx={{ textAlign: 'right' }}>
                Doctor Name: <strong>{doctorName}</strong>
              </Typography>
              <Typography className="fcg-right" sx={{ textAlign: 'right' }}>
                Qualification: <strong>{qualification}</strong>
              </Typography>
              <Typography className="fcg-right" sx={{ mb: 2, textAlign: 'right' }}>
                Registration No: <strong>{regNo}</strong>
              </Typography>
              <Typography className="fcg-right" sx={{ mt: 4, textAlign: 'right' }}>
                Signature &amp; Seal
              </Typography>
            </div>
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default FitnessCertificateGeneral;
